import get from "lodash/get";
import Vue from "vue";
export enum formFieldStatus {
  ready = "",
  success = "success",
  error = "error",
}
export interface FormField {
  prop: string;
  status?: formFieldStatus;
  message?: string;
  rules?: FieldRule[];
}
export interface FormRules {
  [x: string]: FieldRule[];
}
export type Validator = (
  rules: FieldRule[],
  value: unknown,
  callback: (msg?: string) => void
) => void;
export interface FieldRule {
  required?: boolean;
  message?: string;
  validator?: Validator;
}
function isEmpty(val: unknown) {
  if (typeof val === "string" && val === "") {
    return true;
  } else if (Array.isArray(val) && val.length === 0) {
    return true;
  } else if (typeof val === "number" && Number.isNaN(val)) {
    return true;
  } else if (
    val &&
    typeof val === "object" &&
    Object.values(val).every((v) => isEmpty(v))
  ) {
    return true;
  } else {
    return typeof val === "number" ? false : !val;
  }
}
function getFieldRules(field: FormField, formRules: FormRules) {
  return formRules[field.prop] || [];
}
function genValidators(field: FormField, fieldRules: FieldRule[]) {
  const validators: Validator[] = [];
  fieldRules.forEach((fieldRule) => {
    if (typeof fieldRule.validator === "function") {
      validators.push(fieldRule.validator);
    } else if (typeof fieldRule.message === "string") {
      const validator: Validator = (rules, value, callback) => {
        const required = !!fieldRule.required;
        if (required && isEmpty(value)) {
          callback(fieldRule.message || `Prop ${field.prop} is required`);
        } else {
          callback();
        }
      };
      validators.push(validator);
    }
  });
  return validators;
}
function genValidatePromises(
  model: { [x: string]: any },
  field: FormField,
  validators: Validator[]
): Promise<boolean>[] {
  return validators.map((validator) => {
    return new Promise((resolve, reject) => {
      const value = get(model, field.prop);
      console.log("model", model);
      console.log("field.prop)", field.prop);
      const rules = field.rules || [];
      validator(rules, value, (msg?: string) => {
        console.log("msg", msg);
        const success = !msg;
        if (success) {
          // field.status = formFieldStatus.success
          // field.message = ''
          Vue.set(field, "status", formFieldStatus.success);
          Vue.set(field, "message", "");
          resolve(true);
        } else {
          // field.status = formFieldStatus.error
          // field.message = msg
          Vue.set(field, "status", formFieldStatus.error);
          Vue.set(field, "message", msg);
          resolve(false);
        }
      });
    });
  });
}
export class FormModel {
  private model: { [x: string]: any } = {};
  private rules: FormRules = {};
  private fields: { [x: string]: FormField } = {};
  constructor(model?: { [x: string]: any }) {
    if (model && typeof model === "object") {
      this.model = model;
      const fields: { [x: string]: FormField } = {};
      for (const o in model) {
        const field = {
          value: model[o],
          prop: o,
          status: formFieldStatus.ready,
          message: "",
        };
        fields[o] = field;
      }
      Vue.set(this, "fields", fields);
    }
  }
  public static createFields(model: { [x: string]: any }) {
    const fields: FormField[] = [];
    for (const o in model) {
      const field = {
        value: model[o],
        prop: o,
        status: formFieldStatus.ready,
        message: "",
      };
      fields.push(field);
    }
    return fields;
  }
  async validate(callback?: (result: boolean) => void) {
    const res = await this.validateFields(Object.keys(this.fields), callback);
    console.log("validate res", res);
    return res;
  }
  async validateField(prop: string, callback?: (result: boolean) => void) {
    const field = this.fields[prop];
    const fieldRules = field.rules
      ? field.rules
      : getFieldRules(field, this.rules);
    const validators = genValidators(field, fieldRules);
    const validatePromiseList: Promise<boolean>[] = genValidatePromises(
      this.model,
      field,
      validators
    );
    const res = await Promise.all(validatePromiseList);
    const success = res.every((ele) => !!ele);
    if (typeof callback == "function") {
      callback(success);
    }
    return success;
  }
  async validateFields(props: string[], callback?: (result: boolean) => void) {
    const fields: FormField[] = [];
    props.forEach((prop) => {
      const field = this.fields[prop];
      fields.push(field);
    });
    const res = await Promise.all(
      fields.map((field) => this.validateField(field.prop))
    );
    const success = res.every((ele) => !!ele);
    if (typeof callback == "function") {
      callback(success);
    }
    return success;
  }
  setModel(model: { [x: string]: any }) {
    this.model = model;
  }
  setRules(rules: FormRules) {
    this.rules = rules;
  }
  getField(prop: string) {
    return this.fields[prop];
  }
  getFields() {
    return this.fields;
  }
  setFields(fields: { [x: string]: FormField }) {
    Vue.set(this, "fields", fields);
  }
  appendFields(fields: FormField[]) {
    fields.forEach((field) => {
      Vue.set(this.fields, field.prop, field);
    });
  }
  patchFields(fields: FormField[]) {
    fields.forEach((field) => {
      if (!this.fields[field.prop]) {
        Vue.set(this.fields, field.prop, field);
      }
    });
  }
  updateFields(fields: FormField[]) {
    fields.forEach((field) => {
      if (!this.fields[field.prop]) {
        Vue.set(this.fields, field.prop, field);
      }
    });
    for (const o in this.fields) {
      if (!fields.some((field) => field.prop === o)) {
        delete this.fields[o];
      }
    }
  }
  removeField(prop: string) {
    delete this.fields[prop];
  }
  clearValidate(props: string[]) {
    props.forEach((prop) => {
      if (this.fields[prop]) {
        this.fields[prop].message = "";
        this.fields[prop].status = formFieldStatus.ready;
      }
    });
  }
}
